import { FC } from "react";
import { Snackbar, Button, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useRegisterSW } from "virtual:pwa-register/react";
import { startUpdateDiscovery } from "../../utils/pwa/updateDiscovery";

/**
 * Surfaces a "new version available" prompt when a fresh service worker is
 * waiting. Mounted high in the tree (inside SnackBarWrapper) so it works for
 * both the admin and site apps. Uses registerType "prompt" — clicking Reload
 * activates the waiting SW and reloads into the new asset bundle.
 *
 * The prompt MUST carry an explicit dismiss control (the ✕). MUI's `onClose`
 * only fires on clickaway/escape/timeout, none of which reliably happen on an
 * installed PWA (no escape key, unreliable touch clickaway, and we set no
 * auto-hide so a real update isn't missed). Without the ✕ the bar is
 * undismissable on iOS standalone — and Reload doesn't always clear it
 * (a still-waiting SW re-fires `needRefresh`), leaving the user stuck.
 *
 * The browser only checks for a new worker on a page load, so a long-lived
 * installed PWA also gets foreground + hourly checks (#1494); a worker they
 * find surfaces through this same prompt.
 */
export const PwaUpdatePrompt: FC = () => {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW: (_swUrl, registration) => {
      if (registration) startUpdateDiscovery(registration);
    },
  });

  return (
    <Snackbar
      open={needRefresh}
      onClose={() => setNeedRefresh(false)}
      message="A new version of Orgiggly is available."
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      action={
        <>
          <Button
            color="secondary"
            size="small"
            onClick={() => void updateServiceWorker(true)}
          >
            Reload
          </Button>
          <IconButton
            size="small"
            aria-label="Dismiss"
            color="inherit"
            onClick={() => setNeedRefresh(false)}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </>
      }
    />
  );
};
